import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckIcon, ChevronRightIcon } from 'lucide-react';
import { Container, Section, SectionHeader, Kicker, Card, Reveal, Callout } from '../components/ui/Primitives';
import { Button } from '../components/ui/Button';
import { getServiceLine } from '../data/services';
import { ARTICLES } from '../data/insights';
import { Seo } from '../components/Seo';
import { serviceSchema, breadcrumbSchema } from '../lib/seo';
import { NotFound } from './NotFound';
export function ServiceLinePage() {
  const {
    lineId
  } = useParams<{
    lineId: string;
  }>();
  const line = getServiceLine(lineId ?? '');
  if (!line) return <NotFound data-id="element-1513" data-mp="2vriqpmkxcjfn" />;
  const related = ARTICLES.filter(article => article.serviceLine === line.id).slice(0, 3);
  return <>
      <Seo title={`${line.name}: ${line.short.toLowerCase()}`} description={`${line.promise} ${line.outcome}`} path={`/services/${line.id}`} jsonLd={[serviceSchema({
      name: line.name,
      description: `${line.promise} ${line.outcome}`,
      path: `/services/${line.id}`,
      serviceType: line.short,
      audience: line.who,
      offers: line.packages.map(pkg => pkg.name)
    }), breadcrumbSchema([{
      name: 'Home',
      path: '/'
    }, {
      name: 'Services',
      path: '/services'
    }, {
      name: line.name,
      path: `/services/${line.id}`
    }])]} data-id="element-1514" data-mp="3o9o5fcjjo3v3" />

      <section className="surface-dark" data-id="element-1515" data-mp="26mewar6h2o4t">
        <Container className="py-14 sm:py-20" data-id="element-1516" data-mp="26o9g2260ca7b">
          <nav aria-label="Breadcrumb" className="mb-8" data-id="element-1517" data-mp="1rpfhai9ajt0b">
            <ol className="flex flex-wrap items-center gap-2 text-small text-white/55" data-id="element-1518" data-mp="1rqyxy9yv062m">
              <li data-id="element-1519" data-mp="1wpa7skp5mgku"><Link to="/" className="hover:text-white" data-id="element-1520" data-mp="1wp0oth2j19on">Home</Link></li>
              <li aria-hidden="true" data-id="element-1521" data-mp="3m36xk0m4lb4q"><ChevronRightIcon className="h-3.5 w-3.5" data-id="element-1522" data-mp="3m4qabh8kp41r" /></li>
              <li data-id="element-1523" data-mp="3r31o231znyp9"><Link to="/services" className="hover:text-white" data-id="element-1524" data-mp="3r31mhymcbqn5">Services</Link></li>
              <li aria-hidden="true" data-id="element-1525" data-mp="3pl2zjfbw4ykr"><ChevronRightIcon className="h-3.5 w-3.5" data-id="element-1526" data-mp="3plmgottpvixq" /></li>
              <li aria-current="page" className="text-white/80" data-id="element-1527" data-mp="3pj024a8es360">{line.name}</li>
            </ol>
          </nav>
          <Kicker data-id="element-1528" data-mp="3fmwyezxwrwgy">{line.short}</Kicker>
          <h1 className="mt-4 max-w-3xl font-display text-[34px] leading-tight text-white sm:text-[46px]" data-id="element-1529" data-mp="3fnya96mr0mzq">
            {line.name}
          </h1>
          <p className="mt-6 max-w-2xl text-[19px] text-white/80" data-id="element-1530" data-mp="3bnxxdqfasjus">{line.promise}</p>
          <div className="mt-9 flex flex-col gap-3 sm:flex-row" data-id="element-1531" data-mp="c8kgg9yl9vdb">
            <Button to="/contact" size="lg" data-id="element-1532" data-mp="78eoesoeq6up">Book a discovery call</Button>
            <Button to="/augmented-intelligence" variant="reversed" size="lg" data-id="element-1533" data-mp="2g4xpafgx2rd">Read our method</Button>
          </div>
        </Container>
      </section>

      <Section data-id="element-1534" data-mp="3uk79472q8ybm">
        <div className="grid gap-10 lg:grid-cols-2" data-id="element-1535" data-mp="3uimmr4qkglou">
          <div data-id="element-1536" data-mp="uxr3uaabvb5g">
            <Kicker data-id="element-1537" data-mp="uyir8xtru2kn">Who it is for</Kicker>
            <p className="mt-4 text-[17px] text-body" data-id="element-1538" data-mp="q6hfre4fgfvo">{line.who}</p>
          </div>
          <div data-id="element-1539" data-mp="q4ee1wtwwyb4">
            <Kicker data-id="element-1540" data-mp="28t8vgu45vy74">The outcome</Kicker>
            <p className="mt-4 text-[17px] text-body" data-id="element-1541" data-mp="28uajdz6a0f69">{line.outcome}</p>
          </div>
        </div>
      </Section>

      <Section tone="mist" aria-labelledby="sub-services" data-id="element-1542" data-mp="2dsc3mnffukjb">
        <SectionHeader id="sub-services" kicker="What we do" title="Four areas of work" data-id="element-1543" data-mp="2hte417l3d2mv" />
        <div className="mt-10 grid gap-6 sm:grid-cols-2" data-id="element-1544" data-mp="2mt9xicw5xbo6">
          {line.subServices.map((sub, index) => <Reveal key={sub.name} delay={index * 0.05} data-id="element-1545" data-mp="2rsmtbn50ht2w" data-mpi={index}>
              <Card className="h-full" data-id="element-1546" data-mp="2rq0mxqtuxjm9">
                <h3 className="font-display text-[21px] leading-snug text-ink" data-id="element-1547" data-mp="2wq4top4yqzza">{sub.name}</h3>
                <p className="mt-3 text-[15px] text-body" data-id="element-1548" data-mp="31hehrlav5v92">{sub.detail}</p>
              </Card>
            </Reveal>)}
        </div>
      </Section>

      <Section aria-labelledby="use-cases" data-id="element-1549" data-mp="35lcfbrkjiy00">
        <SectionHeader id="use-cases" kicker="In practice" title="Examples by sector" lead="Anonymised until each client agrees to be named. The numbers are theirs, not ours." data-id="element-1550" data-mp="3aj496r4qwu6u" />
        <div className="mt-10 divide-y divide-line border-y border-line" data-id="element-1551" data-mp="31t4c8p2gdwkx">
          {line.useCases.map(useCase => <div key={useCase.sector} className="grid gap-3 py-6 sm:grid-cols-[180px_1fr]" data-id="element-1552" data-mp="2wtjdofr73q8q">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gold" data-id="element-1553" data-mp="3bjqj9uw3tes0">{useCase.sector}</p>
              <p className="text-[16px] text-body" data-id="element-1554" data-mp="36nrqzw7nncmb">{useCase.outcome}</p>
            </div>)}
        </div>
      </Section>

      <Section tone="champagne" aria-labelledby="packages" data-id="element-1555" data-mp="2dx9qn2ybfpcl">
        <SectionHeader id="packages" kicker="Engagements" title={`Working with ${line.name}`} lead="Three ways this line is usually scoped. Every engagement begins with a discovery call and a written proposal, agreed before any work starts." data-id="element-1556" data-mp="2dyumcvwd9ec1" />
        <div className="mt-10 grid gap-6 lg:grid-cols-3" data-id="element-1557" data-mp="81ypcayeqkr6">
          {line.packages.map(option => <Card key={option.name} className="flex h-full flex-col" data-id="element-1558" data-mp="36t3fc509633">
              <h3 className="font-display text-[22px] text-ink" data-id="element-1559" data-mp="ang6f92cwvbi">{option.name}</h3>
              <p className="mt-3 text-[15px] text-body" data-id="element-1560" data-mp="fnkm4wvfxm0o">{option.summary}</p>
              <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-1561" data-mp="1nt1weebz9e8">
                What it includes
              </p>
              <ul className="mt-4 space-y-3 text-[14px] text-body" data-id="element-1562" data-mp="5pmzgdt8bxa6">
                {option.includes.map(item => <li key={item} className="flex gap-3" data-id="element-1563" data-mp="tgse4pnfojiw">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-1564" data-mp="ycqzs5hdnnfm" />
                    {item}
                  </li>)}
              </ul>
              <div className="mt-auto pt-7" data-id="element-1565" data-mp="onh98u26mctm">
                <Button to="/contact" variant="secondary" className="w-full" data-id="element-1566" data-mp="om65gu8ql52i">
                  Discuss this engagement
                </Button>
              </div>
            </Card>)}
        </div>
        <Callout className="mt-8" data-id="element-1567" data-mp="1h6eg1kv6xcul">
          Scope, timing and fees are set out in a written proposal after a discovery call. Nothing on this page is
          financial or legal advice, and Cavalla Solution is not a financial adviser.
        </Callout>
      </Section>

      {related.length > 0 && <Section aria-labelledby="related" data-id="element-1568" data-mp="1i1opivx4aokf">
          <SectionHeader id="related" kicker="Related reading" title={`Insights on ${line.short}`} data-id="element-1569" data-mp="1d1ah3crltclv" />
          <div className="mt-10 grid gap-8 sm:grid-cols-3" data-id="element-1570" data-mp="181o3313ybsc0">
            {related.map(article => <article key={article.slug} data-id="element-1571" data-mp="139mxfy1agzuy">
                <p className="text-small uppercase tracking-[0.12em] text-gold" data-id="element-1572" data-mp="138lb2xetoqxx">{article.sectors[0]}</p>
                <h3 className="mt-3 font-display text-[19px] leading-snug text-ink" data-id="element-1573" data-mp="z6q9drszcbjm">
                  <Link to={`/insights/${article.slug}`} className="underline-offset-4 hover:underline" data-id="element-1574" data-mp="ubufsn83hg4e">
                    {article.title}
                  </Link>
                </h3>
                <p className="mt-3 text-[15px] text-body" data-id="element-1575" data-mp="kcdba94kx8a0">{article.excerpt}</p>
              </article>)}
          </div>
        </Section>}
    </>;
}